import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCategories, createCategory } from '../../../utils/services/categoriesAPI';
import { changeTemplateCategory } from '../../../utils/services/documentAPI';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { SettingsHeading } from '../../SidebarMenu/styles';
import styled from 'styled-components';
import { Button, Form, Input, message, Popover, Select } from 'antd';
import { CATEGORY_INPUT } from './translationValues';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  button {
    border: none;
    outline: none;
    background: transparent;
    color: rgb(223, 76, 53);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }
`;

const SelectStyled = styled(Select)`
  .ant-select-selection--single {
    margin-block: 6px;
    height: 38px;
    padding-block: 3px;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

const { Option } = Select;

const CategoryForm = props => {
  const { t } = useTranslation(['CATEGORY_INPUT']);
  const { form, currentDesign, onCategoryCreated, onClose } = props;
  const { getFieldDecorator, validateFields, resetFields } = form;

  const [creating, setCreating] = useState(false);

  const handleCreate = e => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (err) return;

      setCreating(true);

      try {
        const categoryType = currentDesign === 'document' ? 'template' : 'element';

        const response = await createCategory({
          name: values.categoryName,
          type: categoryType,
        });

        if (response?.statusCode === 501 && response?.id) {
          if (onCategoryCreated) {
            await onCategoryCreated(response.id, values.categoryName);
          }

          resetFields();

          if (onClose) {
            onClose();
          }
        } else {
          message.error(
            t(
              CATEGORY_INPUT.CATEGORY_CREATION_FAILED.key,
              CATEGORY_INPUT.CATEGORY_CREATION_FAILED.value,
            ),
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setCreating(false);
      }
    });
  };

  return (
    <StyledForm onSubmit={handleCreate}>
      <h5>{t(CATEGORY_INPUT.CREATE_CATEGORY.key, CATEGORY_INPUT.CREATE_CATEGORY.value)}</h5>
      <Form.Item>
        {getFieldDecorator('categoryName', {
          rules: [
            {
              required: true,
              message: t(
                CATEGORY_INPUT.CATEGORY_NAME_REQUIRED.key,
                CATEGORY_INPUT.CATEGORY_NAME_REQUIRED.value,
              ),
            },
          ],
        })(
          <Input
            placeholder={t(
              CATEGORY_INPUT.ENTER_CATEGORY_NAME.key,
              CATEGORY_INPUT.ENTER_CATEGORY_NAME.value,
            )}
          />,
        )}
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={creating}>
          {t(CATEGORY_INPUT.CREATE_BUTTON.key, CATEGORY_INPUT.CREATE_BUTTON.value)}
        </Button>
      </Form.Item>
    </StyledForm>
  );
};

const CreateCategory = Form.create()(CategoryForm);

const CategoryInput = ({ templateId, isEmailTemplate = false, isEditMode }) => {
  const { t } = useTranslation(['CATEGORY_INPUT']);
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, ``);

  const categoryIdSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.CATEGORY_ID],
    [],
  );
  const [categoryId, store] = useDocumentContext(categoryIdSelector, `CategoryInput`);

  const [value, setValue] = useState(categoryId);

  const getCategoryType = useCallback(() => {
    if (currentDesign === 'document') {
      return isEmailTemplate ? 'email' : 'document';
    } else if (currentDesign === 'flow') {
      return 'flow';
    } else if (currentDesign === 'block') {
      return isEmailTemplate ? 'standaloneEmailBlock' : 'block';
    }
    return 'document'; // default
  }, [currentDesign, isEmailTemplate]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const includeElements = currentDesign !== 'document';
      const categoryType = currentDesign === 'document' ? 'template' : 'element';

      const response = await getAllCategories(includeElements, categoryType);
      if (response?.statusCode === 507 && response?.data) {
        setCategoryList(response.data);
      } else {
        message.error(
          t(
            CATEGORY_INPUT.FETCH_CATEGORIES_FAILED.key,
            CATEGORY_INPUT.FETCH_CATEGORIES_FAILED.value,
          ),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentDesign]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCategoryChange = async selectedCategoryId => {
    setValue(selectedCategoryId);

    try {
      const type = getCategoryType();
      const response = await changeTemplateCategory(type, {
        templateId: templateId,
        categoryId: selectedCategoryId,
      });
      if (response?.statusCode === 3225) {
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CATEGORY_ID]: selectedCategoryId });
        message.success(
          t(
            CATEGORY_INPUT.CATEGORY_UPDATED_SUCCESS.key,
            CATEGORY_INPUT.CATEGORY_UPDATED_SUCCESS.value,
          ),
        );
      } else {
        message.error(
          t(CATEGORY_INPUT.CATEGORY_UPDATE_FAILED.key, CATEGORY_INPUT.CATEGORY_UPDATE_FAILED.value),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCategoryCreated = async (categoryId, categoryName) => {
    const newCategory = { id: categoryId, name: categoryName };
    setCategoryList(prevList => [...prevList, newCategory]);

    setValue(categoryId);

    try {
      const type = getCategoryType();
      const response = await changeTemplateCategory(type, {
        templateId: templateId,
        categoryId: categoryId,
      });
      if (response?.statusCode === 3225) {
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CATEGORY_ID]: categoryId });
        message.success(
          t(
            CATEGORY_INPUT.CATEGORY_UPDATED_SUCCESS.key,
            CATEGORY_INPUT.CATEGORY_UPDATED_SUCCESS.value,
          ),
        );
      } else {
        message.error(
          t(CATEGORY_INPUT.CATEGORY_UPDATE_FAILED.key, CATEGORY_INPUT.CATEGORY_UPDATE_FAILED.value),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Header>
        <SettingsHeading complexDesign>
          {t(CATEGORY_INPUT.CATEGORY_LABEL.key, CATEGORY_INPUT.CATEGORY_LABEL.value)}
        </SettingsHeading>
        <Popover
          trigger="click"
          placement="left"
          visible={popoverVisible && isEditMode}
          onVisibleChange={visible => {
            if (isEditMode) {
              setPopoverVisible(visible);
            }
          }}
          content={
            <CreateCategory
              currentDesign={currentDesign}
              onCategoryCreated={handleCategoryCreated}
              onClose={() => setPopoverVisible(false)}
            />
          }
        >
          <button disabled={!isEditMode}>
            {t(CATEGORY_INPUT.ADD_NEW_BUTTON.key, CATEGORY_INPUT.ADD_NEW_BUTTON.value)}
          </button>
        </Popover>
      </Header>
      <SelectStyled
        showSearch
        placeholder={t(
          CATEGORY_INPUT.SELECT_CATEGORY_PLACEHOLDER.key,
          CATEGORY_INPUT.SELECT_CATEGORY_PLACEHOLDER.value,
        )}
        optionFilterProp="children"
        value={loading ? undefined : value}
        loading={loading}
        onChange={handleCategoryChange}
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        disabled={loading || !isEditMode}
        style={{ width: '100%' }}
      >
        {categoryList.map(category => (
          <Option key={category.id} value={category.id}>
            {category.name}
          </Option>
        ))}
      </SelectStyled>
    </>
  );
};

export default CategoryInput;
